import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react';
import { cacheDesignMode, hadNoCachedDesignMode, paintedDesignMode } from 'constants/config';
import { useDesignMode } from 'hooks/use-design-mode';
import useStores from 'hooks/use-stores.hooks';
import { applyDesignMode, popSwitchedBackToClassic, shouldHoldFirstPaint } from 'utils/design-mode.utils';
import { NotificationTypes } from 'types/notifications.types';
import { CLASSIC_RETIREMENT_DATE } from 'constants/design-retirement';
import { Loader } from 'components/common/loader';
import styled from 'types/theme.types';

const RESOLVE_TIMEOUT_MS = 5000;

/**
 * `constants/themes.constants` picks its theme objects at module scope, so correcting a wrong first
 * paint means reloading. Making that reactive is APO-484's job, not this one.
 *
 * The session signal is the live `authStore.isAuthenticated`, not a boot-time storage read: on a real
 * first login the callback writes the session mid-load and navigates without a reload, so any value
 * captured at module scope is stale on exactly the load the hold exists for. `observer` is what makes
 * the flip re-render this.
 *
 * Giving up - on the deadline, or on a failure that cannot resolve this session - caches the painted
 * mode and never reloads afterwards: later loads then boot correctly from the cache instead of holding
 * again, and a preference landing after the app is interactive updates the cache for the next load
 * rather than reloading this one out from under the user.
 */
const DesignModeReconciler: React.FunctionComponent<{ children?: ReactNode }> = ({ children }) => {
  const { mode, isResolved, isResolutionBroken } = useDesignMode();
  const { notificationStore, authStore } = useStores();
  const [gaveUp, setGaveUp] = useState(false);
  const hasEverHeld = useRef(false);

  const isHolding = shouldHoldFirstPaint({
    hadNoCache: hadNoCachedDesignMode,
    hasSession: authStore.isAuthenticated,
    isResolved,
    hasWaitedLongEnough: gaveUp
  });
  hasEverHeld.current ||= isHolding;

  // The deadline starts when the hold engages - LaunchDarkly cannot even begin before the session and
  // organizations land, so a clock started at mount would burn on work the hold is not waiting for.
  useEffect(() => {
    if (!isHolding) return;

    const timer = setTimeout(() => setGaveUp(true), RESOLVE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isHolding]);

  useEffect(() => {
    if (isHolding && isResolutionBroken) setGaveUp(true);
  }, [isHolding, isResolutionBroken]);

  // Without this, a load that gave up holds again on every future load, since nothing else writes the
  // cache when resolution never arrives. A persistent LaunchDarkly outage must cost one loader, not one
  // per page load forever.
  useEffect(() => {
    if (gaveUp && hasEverHeld.current) cacheDesignMode(paintedDesignMode);
  }, [gaveUp]);

  useEffect(() => {
    if (!isResolved) return;

    // Past the give-up point the app is interactive, so reloading would land mid-flow - the very thing
    // the hold exists to prevent. Cache the answer instead; the next load paints it natively.
    if (gaveUp) {
      cacheDesignMode(mode);
      return;
    }

    applyDesignMode(mode);
  }, [isResolved, mode, gaveUp]);

  // The click that set this reloaded the page out from under its own toast.
  useEffect(() => {
    if (!popSwitchedBackToClassic()) return;

    notificationStore.setNotification({
      notificationType: NotificationTypes.info,
      message: 'new-look.switched-back',
      messageVariables: { date: CLASSIC_RETIREMENT_DATE }
    });
  }, [notificationStore]);

  if (isHolding) {
    return (
      <FullPageLoader data-e2e="design-mode-settling">
        <Loader />
      </FullPageLoader>
    );
  }

  return <>{children}</>;
};

const FullPageLoader = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
`;

export default observer(DesignModeReconciler);
